import { TabStyles, TextLinkWidget } from '@tableau/widgets';
import React from 'react';

import { CompositeSessionInfoSelectors, SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { siteUrlsSelector } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Formatters, Messages } from '~src/codegen/Localize';
import { openReactActionDialog, ReactActionDialogBodyProps } from '~src/commonInfra/dialogs/ReactActionDialog';
import { imgPath } from '~src/commonInfra/utils/ImgUrl';
import { FlowOutputStepInfo } from '~src/flow/models/FlowOutputStepInfo';
import { RunFlowTaskInfo } from '~src/flow/models/RunFlowTaskInfo';
import { IScheduleInfo } from '~src/schedule/types/IScheduleInfo';
import { classes,cssClass } from '~src/style';

import { RunFlowTasks } from '../resources/RunFlowTasks';
import { editFlowRunTaskDialog } from '../utils/FlowDialogUtils';
import { areFlowParametersEnabled, getParameterParamsFromParameterRuns,getParametersFromOutputSteps } from '../utils/FlowParametersUtils';
import { isFlowSelfServiceSchedulesEnabled } from '../utils/FlowSelfServiceSchedulesUtils';
import { FlowParametersGrid } from './FlowParametersGrid';
import { FlowSubscriptionCount } from './FlowSubscriptionCount';
import { FlowTaskInfoItem } from './FlowTaskInfoItem';
import { FlowTaskOutputsGrid } from './FlowTaskOutputsGrid';

const rootClass = 'FlowTaskOutputsDialog';

export const outerWrapper = cssClass(rootClass, 'outerWrapper', {
  maxHeight: 500,
  overflow: 'auto',
});

export const headerStyle = cssClass(rootClass, 'headerStyle', {
  display: 'flex',
  paddingBottom: 8,
  marginBottom: 12,
  borderBottom: `1px solid ${TabStyles.Colors.L6}`,
  alignItems: 'center',
});

export const scheduleIconWrapper = cssClass(rootClass, 'scheduleIconWrapper', {
  display: 'flex',
  alignItems: 'center',
  fontWeight: 500,
  marginRight: 8,
});

export const scheduleIcon = cssClass(rootClass, 'scheduleIcon', {
  height: 20,
  width: 20,
  marginRight: 4,
});

const expansionWrapper = cssClass(rootClass, 'expansionWrapper', {
  border: `1px solid ${TabStyles.Colors.L6}`,
  borderTop: 0,
  padding: 12,
  $nest: {
    '& .tb-react-dg-hrow': { borderTop: 0 }
  },
});

const gridHeader = cssClass(rootClass, 'gridHeader', {
  fontWeight: 500,
  marginBottom: 8,
});

const topPadding = cssClass(rootClass, 'topPadding', {
  paddingTop: 8,
});

const sectionBorder = cssClass(rootClass, 'sectionBorder', {
  border: `1px solid ${TabStyles.Colors.L6}`,
  boxSizing: 'border-box',
  height: 36,
});

const countWrapper = cssClass(rootClass, 'countWrapper', {
  height: 36,
  lineHeight: '36px',
  paddingLeft: 12,
});

function onAction(props: FlowTaskOutputsDialogProps) {
  return editFlowRunTaskDialog(props.flowTask);
}

export function openFlowTaskOutputsDialog(props: FlowTaskOutputsDialogProps) {
  const outputStepCount = props.flowTask.flowOutputSteps.length;
  const modalInstance = openReactActionDialog({
    title: Messages.outputsCount({COUNT: outputStepCount, COUNT_LOC: Formatters.formatCount(outputStepCount)}),
    bodyComponent: FlowTaskOutputsDialogContent,
    initialBodyProps: {
      ...props,
    },
    initialDialogState: {},
    actionName: Messages.editTask(),
    action: onAction,
    dialogWidth: 800,
    dismissDialogOnActionFailed: false,
  });

  return modalInstance.result;
}

export interface FlowTaskOutputsDialogProps {
  flowTask: RunFlowTaskInfo;
  outputSteps: FlowOutputStepInfo[];
}

export type FlowTaskOutputsActionDialogProps = ReactActionDialogBodyProps<FlowTaskOutputsDialogProps, {}>;

export function FlowTaskOutputsDialogContent(props: FlowTaskOutputsActionDialogProps): JSX.Element {
  const [subscriptionCount, setSubscriptionCount] = React.useState<number | null>(null);

  const flowOutputSubscriptionsEnabledOnSite = SessionSiteSelectors.isFlowOutputSubscriptionsEnabled(getAppStore().getState());

  React.useEffect(() => {
    if (props.flowTask.id && flowOutputSubscriptionsEnabledOnSite) {
      RunFlowTasks.getSubscriptionFlow(props.flowTask.id).then((result) => {
        if (result?.subscriptionFlow?.subscriptionFlowMetadata?.subscriberCount > 0) {
          setSubscriptionCount(result.subscriptionFlow.subscriptionFlowMetadata.subscriberCount);
        }
      });
    }
  },[props.flowTask.id, flowOutputSubscriptionsEnabledOnSite]);


  function renderScheduleText(): JSX.Element {
    const siteUrls = siteUrlsSelector(getAppStore().getState());
    const isUserSiteOrServerAdmin = CompositeSessionInfoSelectors.currentUserIsSiteOrServerAdminSelector(getAppStore().getState());
    const scheduleDisplay = isFlowSelfServiceSchedulesEnabled() ?
      IScheduleInfo.getScheduleDescription(props.flowTask.schedule) :
      props.flowTask.schedule.name;
    return isUserSiteOrServerAdmin ?
      <TextLinkWidget
        href={siteUrls.scheduleFlowsUrl(props.flowTask.schedule.id)}
        title={props.flowTask.schedule.name}
        testId={`flow-task-outputs-dialog-link-${props.flowTask.schedule.id}`}
      >
        {scheduleDisplay}
      </TextLinkWidget>
    :
      <div title={props.flowTask.schedule.name}>
        {scheduleDisplay}
      </div>;
  }

  function renderOutputsGrid(): JSX.Element {
    return (
      <div>
        <div className={gridHeader}>{Messages.flowOutputsHeader()}</div>
        <FlowTaskOutputsGrid outputIds={outputIds} outputSteps={props.outputSteps} />
      </div>
    );
  }

  function renderParametersGrid(): JSX.Element {
    const parameters = getParametersFromOutputSteps(props.outputSteps);
    return parameters.length > 0 && (
      <div className={topPadding}>
        <div className={gridHeader}>{Messages.flowParametersHeader()}</div>
        <FlowParametersGrid parameters={parameters} parameterParams={getParameterParamsFromParameterRuns(props.flowTask.relevantParameterRuns)}/>
      </div>
    );
  }

  function renderSubscriptionCount() {
    return (
      <>
        <div className={classes(gridHeader, topPadding)}>{Messages.subscriptions()}</div>
        <div className={sectionBorder}>
          <div className={countWrapper}>
            <FlowSubscriptionCount
              count={subscriptionCount}
              showCheckmark={false}
              flowId={props.flowTask.flowId}
            />
          </div>
        </div>
      </>
    );
  }

  function renderExpansionContent(): JSX.Element {
    return (
      <div className={expansionWrapper}>
        {renderOutputsGrid()}
        {areFlowParametersEnabled() && renderParametersGrid()}
        {flowOutputSubscriptionsEnabledOnSite && subscriptionCount && subscriptionCount > 0 && renderSubscriptionCount()}
      </div>
    );
  }

  const outputIds = props.flowTask.flowOutputSteps.map(outputStep => outputStep.id);

  return (
    <div className={outerWrapper}>
      <div className={headerStyle}>
        <div className={scheduleIconWrapper}>
          <img src={imgPath('schedule_content_type_small.svg')} className={scheduleIcon} alt={''} />
          {Messages.schedule()}
        </div>
        <div>{renderScheduleText()}</div>
      </div>
      <FlowTaskInfoItem
        taskId={props.flowTask.id}
        flowName={props.flowTask.flow.name}
        flowId={props.flowTask.flow.id}
        lastRun={props.flowTask.lastRunAt}
        taskStatus={props.flowTask.status}
        expansionContent={renderExpansionContent()}
        expansionState={'static'}
      />
    </div>
  );
}
